import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { LangCode } from "../data/types.js";
import { t } from "../i18n/strings.js";

const PROMOS = [
  { icon: "⭐", key: "promo.reviews" },
  { icon: "👘", key: "promo.rent" },
  { icon: "📸", key: "promo.photoshoot" },
  { icon: "💇‍♀️", key: "promo.hair" },
  { icon: "🚶", key: "promo.walk" },
  { icon: "⛩️", key: "promo.temple" },
];

const ROTATE_MS = 5000;

interface Props {
  hidden?: boolean;
  lang?: LangCode;
}

export function PromoPill({ hidden = false, lang = "en" }: Props) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (hidden) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % PROMOS.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [hidden]);

  const current = PROMOS[index];

  return (
    <AnimatePresence>
      {!hidden && (
        <motion.div
          className="fixed bottom-0 left-0 right-0 z-30 pointer-events-none"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <div
            className="pointer-events-auto"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            <a
              href="https://price.kimonorentalann.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between mx-3 mb-2 px-4 py-2.5 rounded-2xl no-underline"
              style={{
                background: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(16px) saturate(1.4)",
                WebkitBackdropFilter: "blur(16px) saturate(1.4)",
                boxShadow: "0 2px 16px rgba(0,0,0,0.1)",
              }}
            >
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <span className="text-base flex-shrink-0">{current.icon}</span>
                <div className="min-w-0 flex-1" style={{ height: 34 }}>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={`${index}-${lang}`}
                      className="text-[13px] font-medium text-ink-0 leading-[17px]"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.25 }}
                    >
                      {t(current.key, lang)}
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>
              <span
                className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-xl text-white ml-3"
                style={{ backgroundColor: "#B76E6E" }}
              >
                {t("promo.viewPlans", lang)}
              </span>
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
